import { DestroyRef, Directive, inject, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PackageFeature, ProductPackageService } from '@twaice-fe/frontend/shared/services';
import { combineLatest, map, ReplaySubject, switchMap } from 'rxjs';

/**
 * Renders its content only while the customer's product package includes the feature,
 * the template counterpart of the `packageIncludes` route guard.
 *
 * A package that cannot be resolved counts as the full one, so content is shown rather
 * than withheld while ui-config is still in flight.
 *
 * Passing several features renders the content when *any* of them is included — for
 * sections whose children are gated individually.
 *
 * @example
 * ```html
 * <!-- dropped entirely when the package excludes Anomalies -->
 * <li *twPackageFeature="PackageFeature.Anomalies">Anomalies</li>
 *
 * <!-- replaced by an upsell teaser instead of dropped -->
 * <li *twPackageFeature="PackageFeature.Anomalies; fallback: anomaliesUpsellTpl">Anomalies</li>
 * <ng-template #anomaliesUpsellTpl><li>Anomalies (upgrade)</li></ng-template>
 *
 * <!-- kept while at least one of the section's pages is included -->
 * <li *twPackageFeature="[PackageFeature.RecoverableEnergy, PackageFeature.Performance]">…</li>
 * ```
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[twPackageFeature]',
  standalone: true,
})
export class PackageFeatureDirective {
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly productPackageService = inject(ProductPackageService);

  private readonly features$ = new ReplaySubject<PackageFeature[]>(1);

  private fallbackTemplateRef: TemplateRef<unknown> | null = null;
  /** `null` until the package is known, so the fallback setter cannot render prematurely. */
  private isIncluded: boolean | null = null;
  private renderedTemplateRef: TemplateRef<unknown> | null = null;

  constructor() {
    this.features$
      .pipe(
        switchMap((features) =>
          combineLatest(features.map((feature) => this.productPackageService.hasFeature(feature))).pipe(
            map((included) => included.some(Boolean))
          )
        ),
        takeUntilDestroyed(inject(DestroyRef))
      )
      .subscribe((isIncluded) => {
        this.isIncluded = isIncluded;
        this.render(isIncluded);
      });
  }

  @Input({ required: true })
  set twPackageFeature(feature: PackageFeature | PackageFeature[]) {
    this.features$.next(Array.isArray(feature) ? feature : [feature]);
  }

  /** Rendered in place of the content when the feature is not included; dropped when omitted. */
  @Input()
  set twPackageFeatureFallback(fallbackTemplateRef: TemplateRef<unknown> | null) {
    this.fallbackTemplateRef = fallbackTemplateRef ?? null;
    if (this.isIncluded !== null) this.render(this.isIncluded);
  }

  private render(isIncluded: boolean): void {
    const templateRef = isIncluded ? this.templateRef : this.fallbackTemplateRef;
    if (templateRef === this.renderedTemplateRef) return;

    this.viewContainerRef.clear();
    this.renderedTemplateRef = templateRef;
    if (templateRef) this.viewContainerRef.createEmbeddedView(templateRef);
  }
}
